import { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { VatStatus } from '@jalupro/shared';
import { api, http, useApi, useMutation } from '../../api';
import type { ClientView } from '../../api';
import { LoadingState, ErrorState, EmptyState, VatDot, SearchIcon } from '../../components';
import { useT, useLang } from '../../i18n';
import type { TFunc } from '../../i18n';
import dict from './Clients.i18n';
import { MODULES } from '../../modules';
import { countryName, countryFlag } from '../../lib/countries';

/** Map a VAT status to its localized tooltip key (validity dot hover). */
const VAT_LABEL_KEY: Record<VatStatus, string> = {
  [VatStatus.VALID]: 'vatValid',
  [VatStatus.INVALID]: 'vatInvalid',
  [VatStatus.UNAVAILABLE]: 'vatUnavailable',
  [VatStatus.UNVERIFIED]: 'vatUnverified',
  [VatStatus.NOT_APPLICABLE]: 'vatNotApplicable',
};

/** Compose the dot tooltip: localized status, plus the last-checked date when known. */
function vatTitle(c: ClientView, t: TFunc): string {
  const label = t(VAT_LABEL_KEY[c.vatStatus]);
  if (c.vatCheckedAt) {
    return `${label} · ${t('vatCheckedOn', {
      date: new Date(c.vatCheckedAt).toLocaleDateString(),
    })}`;
  }
  return label;
}

/**
 * Agent clients directory — route /agent/clients.
 * Built from Screen4B_ClientsClean (design-source/screen-4-clients.jsx).
 *
 * Renders only the content to the right of the sidebar (the AgentLayout supplies
 * <CleanSidebar> and the `col grow` page container). Hardcoded rows from the
 * wireframe are replaced with live data from GET /clients. "+ New client" and the
 * row "Edit" action navigate to the full-page ClientForm (/agent/clients/new and
 * /agent/clients/:id/edit). "Import CSV" POSTs to /clients/import.
 */

const PAGE_SIZE = 50;

function initials(name: string): string {
  return name
    .split(' ')
    .map((s) => s[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

function primaryEmail(c: ClientView): string {
  return c.emails[0] ?? '—';
}

const PLACEHOLDER = '—';

/** Date of the client's most recent order (localized short date), or a dash. */
function lastOrderLabel(c: ClientView, lang: string): string {
  const iso = c.totals?.lastOrderAt;
  if (!iso) return PLACEHOLDER;
  return new Date(iso).toLocaleDateString(lang, { day: 'numeric', month: 'short' });
}

/** Euro total from integer cents, e.g. 231000 → "€2,310". */
function euros(cents: number): string {
  return `€${Math.round(cents / 100).toLocaleString('en-GB')}`;
}

export default function AgentClients() {
  const t = useT(dict);
  const { lang } = useLang();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [notice, setNotice] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data, loading, error, reload } = useApi(
    () => api.clients.list({ page: 1, pageSize: PAGE_SIZE }),
    [],
  );

  // The frozen api client exposes no clients.import method, so the import goes
  // through the low-level typed http helper (see deviations re: multipart).
  const importMutation = useMutation(importClients);
  const removeMutation = useMutation(api.clients.requestRemoval);

  const clients = data?.items ?? [];

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return clients;
    return clients.filter(
      (c) =>
        c.company.toLowerCase().includes(q) ||
        c.cifVat.toLowerCase().includes(q) ||
        c.emails.some((e) => e.toLowerCase().includes(q)) ||
        (c.billingAddress?.city ?? '').toLowerCase().includes(q),
    );
  }, [clients, query]);

  const needingCorrection = useMemo(() => clients.filter((c) => c.needsCorrection).length, [clients]);

  const subtitle = data
    ? t(data.total === 1 ? 'subtitleClinic_one' : 'subtitleClinic_other', {
        count: data.total,
      })
    : t('subtitleAddressBook');

  function onPickFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const content = typeof reader.result === 'string' ? reader.result : '';
      importMutation
        .mutate({ filename: file.name, content })
        .then(() => reload())
        .catch(() => {
          /* importMutation.error is surfaced below */
        });
    };
    reader.readAsText(file);
  }

  // An agent never deletes a client (Area 1 F-4): they raise a removal request with a
  // reason, which a super-admin actions.
  function onRequestRemoval(c: ClientView) {
    const reason = window.prompt(t('requestRemovalPrompt', { name: c.company }));
    if (reason === null) return;
    setNotice(null);
    removeMutation
      .mutate(c.id, reason.trim())
      .then(() => {
        setNotice(t('requestRemovalDone', { name: c.company }));
      })
      .catch(() => {
        /* removeMutation.error is surfaced below */
      });
  }

  return (
    <>
      {/* Topbar */}
      <div
        className="row center between"
        style={{ padding: '14px 22px', borderBottom: '1px solid var(--line)', background: '#fff' }}
      >
        <div className="col">
          <div className="meta">{subtitle}</div>
          <div className="h1">{t('heading')}</div>
        </div>
        <div className="row gap-8 center">
          <label className="input" style={{ width: 240 }}>
            <SearchIcon />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t('searchPlaceholder')}
              aria-label={t('searchPlaceholder')}
              style={{
                border: 'none',
                outline: 'none',
                background: 'transparent',
                font: 'inherit',
                color: 'var(--ink)',
                width: '100%',
              }}
            />
          </label>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,text/csv"
            onChange={onPickFile}
            style={{ display: 'none' }}
          />
          <button
            type="button"
            className="btn"
            disabled={importMutation.loading}
            onClick={() => fileInputRef.current?.click()}
          >
            {importMutation.loading ? t('importingBtn') : t('importCsvBtn')}
          </button>
          <button
            type="button"
            className="btn primary"
            onClick={() => navigate('/agent/clients/new')}
          >
            {t('newClientBtn')}
          </button>
        </div>
      </div>

      {/* Content */}
      <div style={{ padding: '14px 22px', overflow: 'auto', flex: 1 }}>
        {importMutation.error && (
          <div className="mb-12">
            <ErrorState message={importMutation.error.message} />
          </div>
        )}
        {removeMutation.error && (
          <div className="mb-12">
            <ErrorState message={removeMutation.error.message} />
          </div>
        )}
        {notice && (
          <div
            className="panel mb-12"
            style={{ padding: '10px 14px', background: 'var(--amber-bg)', border: '1px solid var(--amber-line)' }}
          >
            <span className="meta" style={{ fontSize: 13, color: 'var(--amber)' }}>
              {notice}
            </span>
          </div>
        )}

        <div className="panel">
          {loading ? (
            <LoadingState label={t('loadingClients')} />
          ) : error ? (
            <div style={{ padding: 14 }}>
              <ErrorState message={error.message} onRetry={reload} />
            </div>
          ) : filtered.length === 0 ? (
            <EmptyState
              title={query ? t('noMatchTitle') : t('emptyTitle')}
              hint={query ? t('noMatchHint') : t('emptyHint')}
            />
          ) : (
            <table>
              <thead>
                <tr>
                  <th></th>
                  <th>{t('colClient')}</th>
                  <th>{t('colStatus')}</th>
                  <th>{t('colCity')}</th>
                  <th>{t('colCountry')}</th>
                  <th>{t('colEmail')}</th>
                  <th>{t('colCifVat')}</th>
                  <th>{t('colVat')}</th>
                  <th style={{ textAlign: 'right' }}>{t('colOrders')}</th>
                  <th>{t('colLast')}</th>
                  <th style={{ textAlign: 'right' }}>{t('colLifetime')}</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((c, i) => (
                  <tr key={c.id}>
                    <td>
                      <div
                        style={{
                          width: 30,
                          height: 30,
                          background: i % 2 ? 'var(--line-2)' : 'var(--line)',
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: 12,
                          fontWeight: 600,
                          color: 'var(--muted)',
                        }}
                      >
                        {initials(c.company)}
                      </div>
                    </td>
                    <td style={{ fontWeight: 500 }}>{c.company}</td>
                    <td>
                      <span
                        className={`pill ${c.blockedForOrders ? 'red' : 'green'} dot`}
                        title={c.correctionReasons.join(' · ')}
                      >
                        {c.blockedForOrders ? t('statusBlocked') : t('statusOk')}
                      </span>
                    </td>
                    <td className="meta">{c.billingAddress?.city || PLACEHOLDER}</td>
                    <td className="meta">
                      {c.billingAddress?.countryCode
                        ? `${countryFlag(c.billingAddress.countryCode)} ${countryName(c.billingAddress.countryCode, lang)}`
                        : PLACEHOLDER}
                    </td>
                    <td className="mono" style={{ fontSize: 12, color: 'var(--muted)' }}>
                      {primaryEmail(c)}
                    </td>
                    <td className="mono" style={{ fontSize: 12, color: 'var(--muted)' }}>
                      {c.cifVat || PLACEHOLDER}
                    </td>
                    <td>
                      <VatDot
                        vatNumber={c.vatNumber}
                        status={c.vatStatus}
                        title={vatTitle(c, t)}
                        placeholder={PLACEHOLDER}
                      />
                    </td>
                    <td className="mono" style={{ textAlign: 'right' }}>
                      {c.totals?.orderCount ?? 0}
                    </td>
                    <td className="meta">{lastOrderLabel(c, lang)}</td>
                    <td className="mono" style={{ textAlign: 'right', fontWeight: 500 }}>
                      {euros(c.totals?.lifetimeTotalCents ?? 0)}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <div className="row gap-8 end">
                        {MODULES.agentPortal && (
                          <button
                            type="button"
                            className="btn sm"
                            onClick={() => navigate(`/agent/orders/new?clientId=${c.id}`)}
                          >
                            {t('newOrderBtn')}
                          </button>
                        )}
                        <button
                          type="button"
                          className="btn sm"
                          onClick={() => navigate(`/agent/clients/${c.id}/edit`)}
                        >
                          {t('editBtn')}
                        </button>
                        <button
                          type="button"
                          className="btn sm"
                          disabled={removeMutation.loading}
                          onClick={() => onRequestRemoval(c)}
                        >
                          {t('requestRemovalBtn')}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Tips + Missing-data panels */}
        <div className="row gap-12 mt-16">
          <div className="panel grow" style={{ padding: 14 }}>
            <div className="h3 mb-8">{t('tipsHeading')}</div>
            <div className="meta" style={{ fontSize: 13 }}>
              {t('tipsCopy')}
            </div>
          </div>
          <div className="panel" style={{ width: 300, padding: 14 }}>
            <div className="h3 mb-8">{t('missingDataHeading')}</div>
            <div className="col gap-6">
              {needingCorrection > 0 ? (
                <div className="row between">
                  <span style={{ fontSize: 13 }}>
                    {t(needingCorrection === 1 ? 'needsCorrection_one' : 'needsCorrection_other', {
                      count: needingCorrection,
                    })}
                  </span>
                  <span className="pill red">fix</span>
                </div>
              ) : (
                <div className="meta" style={{ fontSize: 13 }}>
                  {t('allClientsOk')}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

/** POST /clients/import — the frozen typed client carries no import method, so we
 * reach the low-level helper here. The client only sends JSON, so we ship the CSV
 * text as `{ filename, content }` rather than multipart (see deviations). */
function importClients(body: { filename: string; content: string }): Promise<unknown> {
  return http.post<unknown>('/clients/import', body);
}
